import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import AuthCallback from "@/components/auth/AuthCallback";

import LoginSelection from "@/pages/auth/LoginSelection";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import Unauthorized from "@/pages/auth/Unauthorized";

import DashboardLayout from "@/components/layout/DashboardLayout";
import PlaceholderPage from "@/components/dashboard/PlaceholderPage";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminCourses from "@/pages/admin/AdminCourses";
import AdminProfile from "@/pages/admin/AdminProfile";
import AdminSettings from "@/pages/admin/AdminSettings";
import ManageClasses from "@/pages/admin/ManageClasses";
import ManageCourses from "@/pages/admin/ManageCourses";
import ManageSubjects from "@/pages/admin/ManageSubjects";
import ManageChapters from "@/pages/admin/ManageChapters";
import ManageSchools from "@/pages/admin/ManageSchools";
import ManageSchoolAdmins from "@/pages/admin/ManageSchoolAdmins";
import ManageStudents from "@/pages/admin/ManageStudents";
import ManageProducts from "@/pages/admin/ManageProducts";
import ManageOrders from "@/pages/admin/ManageOrders";
import ManageFunHub from "@/pages/admin/ManageFunHub";
import ManageNotifications from "@/pages/admin/ManageNotifications";

import InstructorDashboard from "@/pages/instructor/InstructorDashboard";
import InstructorCourses from "@/pages/instructor/InstructorCourses";
import InstructorStudents from "@/pages/instructor/InstructorStudents";
import { InstructorClasses, InstructorCoursesView, InstructorSubjects, InstructorChapters } from "@/pages/instructor/InstructorViewOnly";
import { InstructorQuizzes, InstructorQuizResults, InstructorResults } from "@/pages/instructor/InstructorAssessments";

import StudentDashboard from "@/pages/student/StudentDashboard";
import StudentCourses from "@/pages/student/StudentCourses";
import StudentSiteLayout from "@/components/layout/StudentSiteLayout";
// Code-split the student site routes — these screens are larger and don't need to ship in the main bundle.
const StudentHome = lazy(() => import("@/pages/student-site/StudentHome"));
const StudentClassroom = lazy(() => import("@/pages/student-site/StudentClassroom"));
const StudentSubject = lazy(() => import("@/pages/student-site/StudentClassroom").then((m) => ({ default: m.SubjectList })));
const StudentChapter = lazy(() => import("@/pages/student-site/StudentClassroom").then((m) => ({ default: m.ChapterViewer })));
// const StudentSubscription = lazy(() => import("@/pages/student-site/StudentSubscription"));
const StudentShop = lazy(() => import("@/pages/student-site/StudentShop"));
const StudentOrders = lazy(() => import("@/pages/student-site/StudentOrders"));
const StudentFunHub = lazy(() => import("@/pages/student-site/StudentFunHub"));
const StudentNotifications = lazy(() => import("@/pages/student-site/StudentNotifications"));

function StudentSiteFallback() {
  return (
    <div className="h-[60vh] w-full flex items-center justify-center" data-testid="student-route-loading">
      <div className="flex items-center gap-3 text-[var(--cms-teal-deep)]">
        <span className="w-2.5 h-2.5 rounded-full bg-[var(--cms-teal)] animate-bounce" style={{ animationDelay: "0ms" }} />
        <span className="w-2.5 h-2.5 rounded-full bg-[var(--cms-yellow)] animate-bounce" style={{ animationDelay: "120ms" }} />
        <span className="w-2.5 h-2.5 rounded-full bg-[var(--cms-red)] animate-bounce" style={{ animationDelay: "240ms" }} />
        <span className="text-xs uppercase tracking-widest ml-2">Loading…</span>
      </div>
    </div>
  );
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={`/${user.role}/dashboard`} replace />;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: process OAuth callback before routing to avoid race conditions
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      {/* Public auth routes */}
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/login" element={<LoginSelection />} />
      <Route path="/login/:role" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/unauthorized" element={<Unauthorized />} />

      {/* Admin */}
      <Route path="/admin" element={<ProtectedRoute allow={["admin"]}><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="profile" element={<AdminProfile />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="courses" element={<ManageCourses />} />
        <Route path="courses/legacy" element={<AdminCourses />} />
        <Route path="classes" element={<ManageClasses />} />
        <Route path="subjects" element={<ManageSubjects />} />
        <Route path="chapters" element={<ManageChapters />} />
        <Route path="schools" element={<ManageSchools />} />
        <Route path="school-admins" element={<ManageSchoolAdmins />} />
        <Route path="students" element={<ManageStudents />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="classes" element={<ManageClasses />} />
        <Route path="quiz" element={<PlaceholderPage eyebrow="Resources" title="Quizzes" subtitle="Author graded assessments and auto-mark." bullets={["MCQ, short answer, code","Timed mode","Plagiarism flags"]} />} />
        <Route path="plans" element={<PlaceholderPage eyebrow="Store" title="Plans & Subscriptions" subtitle="Configure pricing tiers, trials and coupons." />} />
        <Route path="products" element={<ManageProducts />} />
        <Route path="payments" element={<PlaceholderPage eyebrow="Store" title="Payments" subtitle="Payouts, refunds, and reconciliation." />} />
        <Route path="orders" element={<ManageOrders />} />
        <Route path="fun-hub" element={<ManageFunHub />} />
        <Route path="notifications" element={<ManageNotifications />} />
        <Route path="analytics" element={<PlaceholderPage eyebrow="Analytics" title="Platform analytics" subtitle="Revenue, growth, cohort retention." />} />
        <Route path="emails" element={<PlaceholderPage eyebrow="Communication" title="Emails" subtitle="Design and schedule marketing & transactional emails." />} />
        <Route path="announcements" element={<PlaceholderPage eyebrow="Communication" title="Announcements" subtitle="Broadcast to instructors or students." />} />
        <Route path="support" element={<PlaceholderPage eyebrow="Support" title="Support center" subtitle="Help content, feedback and tickets." />} />
      </Route>

      {/* Instructor */}
      <Route path="/instructor" element={<ProtectedRoute allow={["instructor", "admin"]}><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/instructor/dashboard" replace />} />
        <Route path="dashboard" element={<InstructorDashboard />} />
        <Route path="profile" element={<AdminProfile />} />
        <Route path="students" element={<InstructorStudents />} />
        <Route path="classes" element={<InstructorClasses />} />
        <Route path="courses" element={<InstructorCoursesView />} />
        <Route path="courses/legacy" element={<InstructorCourses />} />
        <Route path="subjects" element={<InstructorSubjects />} />
        <Route path="chapters" element={<InstructorChapters />} />
        <Route path="quizzes" element={<InstructorQuizzes />} />
        <Route path="quiz-results" element={<InstructorQuizResults />} />
        <Route path="results" element={<InstructorResults />} />
        <Route path="assignments" element={<PlaceholderPage eyebrow="Grading" title="Assignments" subtitle="Create, grade and give feedback." />} />
        <Route path="live" element={<PlaceholderPage eyebrow="Live" title="Live classes" subtitle="Host and schedule live sessions." />} />
        <Route path="analytics" element={<PlaceholderPage eyebrow="Insight" title="Analytics" subtitle="Performance trends across your courses." />} />
        <Route path="announcements" element={<PlaceholderPage eyebrow="Broadcast" title="Announcements" subtitle="Talk to all students at once." />} />
        <Route path="settings" element={<PlaceholderPage eyebrow="You" title="Instructor settings" subtitle="Profile, payouts and notifications." />} />
      </Route>

      {/* Student site — new color-rich layout */}
      <Route path="/student" element={<ProtectedRoute allow={["student", "admin"]}><StudentSiteLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/student/dashboard" replace />} />
        <Route path="dashboard" element={<Suspense fallback={<StudentSiteFallback />}><StudentHome /></Suspense>} />
        <Route path="classroom" element={<Suspense fallback={<StudentSiteFallback />}><StudentClassroom /></Suspense>} />
        <Route path="classroom/:courseId" element={<Suspense fallback={<StudentSiteFallback />}><StudentSubject /></Suspense>} />
        <Route path="classroom/:courseId/subjects/:subjectId" element={<Suspense fallback={<StudentSiteFallback />}><StudentChapter /></Suspense>} />
        {/* <Route path="subscription" element={<Suspense fallback={<StudentSiteFallback />}><StudentSubscription /></Suspense>} /> */}
        <Route path="shop" element={<Suspense fallback={<StudentSiteFallback />}><StudentShop /></Suspense>} />
        <Route path="order" element={<Suspense fallback={<StudentSiteFallback />}><StudentOrders /></Suspense>} />
        <Route path="fun-hub" element={<Suspense fallback={<StudentSiteFallback />}><StudentFunHub /></Suspense>} />
        <Route path="notifications" element={<Suspense fallback={<StudentSiteFallback />}><StudentNotifications /></Suspense>} />
        <Route path="profile" element={<AdminProfile />} />
      </Route>

      {/* Legacy student gamified dashboard (kept for compatibility) */}
      <Route path="/student/legacy" element={<ProtectedRoute allow={["student", "admin"]}><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<StudentDashboard />} />
        <Route path="courses" element={<StudentCourses />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "rgba(11,17,32,0.95)", color: "#fff", border: "1px solid rgba(255,255,255,0.1)", backdropFilter: "blur(20px)" } }} />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

